import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Heart, Share2, MessageCircle, Volume2, VolumeX, 
  Play, Pause, ChevronUp, ChevronDown, Settings,
  Grid, Music, Gamepad2, Tv, Trophy, Newspaper,
  GraduationCap, Cpu, Wrench, Users, Laugh, X,
  Plus, ExternalLink, Loader2, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/UserAuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Category icons mapping
const categoryIcons = {
  all: Grid,
  music: Music,
  gaming: Gamepad2,
  entertainment: Tv,
  comedy: Laugh,
  sports: Trophy,
  news: Newspaper,
  education: GraduationCap,
  tech: Cpu,
  howto: Wrench,
  people: Users
};

const Doomscroll = () => {
  const { user } = useAuth();
  const userId = user?.user_id;
  
  // State
  const [shorts, setShorts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  
  // Refs
  const containerRef = useRef(null);
  const videoRefs = useRef({});
  const observerRef = useRef(null);
  
  // Fetch categories on mount
  useEffect(() => {
    console.log('Doomscroll mounted, fetching categories...');
    fetchCategories();
  }, []);
  
  // Fetch shorts on mount and when category changes
  useEffect(() => {
    console.log('Doomscroll useEffect triggered - userId:', userId, 'selectedCategory:', selectedCategory);
    setShorts([]);
    setNextPageToken(null);
    setCurrentIndex(0);
    fetchShorts();
    if (userId) {
      fetchFavorites();
    }
  }, [userId, selectedCategory]);
  
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/doomscroll/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };
  
  const fetchShorts = async (pageToken = null) => {
    console.log('fetchShorts called - pageToken:', pageToken, 'API_URL:', API_URL);
    
    if (pageToken) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      const params = new URLSearchParams({
        user_id: userId || 'anonymous',
        max_results: '20'
      });
      
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      
      if (pageToken) {
        params.append('page_token', pageToken);
      }
      
      const url = `${API_URL}/api/doomscroll/shorts?${params}`;
      console.log('Fetching from URL:', url);
      
      const res = await fetch(url);
      console.log('Response status:', res.status);
      
      if (res.ok) {
        const data = await res.json();
        console.log('Shorts response - success:', data.success, 'count:', data.shorts?.length);
        if (pageToken) {
          setShorts(prev => [...prev, ...(data.shorts || [])]);
        } else {
          setShorts(data.shorts || []);
        }
        setNextPageToken(data.next_page_token);
      } else {
        const errData = await res.json();
        console.error('Shorts fetch error:', errData);
        toast.error(errData.detail || 'Failed to fetch shorts');
      }
    } catch (err) {
      console.error('Failed to fetch shorts:', err);
      toast.error('Failed to load videos - check console for details');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const fetchFavorites = async () => {
    if (!userId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/doomscroll/favorites?user_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setFavorites(data.favorites || []);
      }
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
    }
  };
  
  const toggleFavorite = async (short) => {
    if (!userId) return;
    
    const isFavorited = short.is_favorited || favorites.some(f => f.video_id === short.id);
    
    try {
      if (isFavorited) {
        const res = await fetch(
          `${API_URL}/api/doomscroll/favorites/${short.id}?user_id=${userId}`,
          { method: 'DELETE' }
        );
        if (res.ok) {
          setFavorites(prev => prev.filter(f => f.video_id !== short.id));
          setShorts(prev => prev.map(s => 
            s.id === short.id ? { ...s, is_favorited: false } : s
          ));
          toast.success('Removed from favorites');
        }
      } else {
        const res = await fetch(
          `${API_URL}/api/doomscroll/favorites?user_id=${userId}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              video_id: short.id,
              title: short.title,
              channel_title: short.channel_title,
              thumbnail_url: short.thumbnail_url,
              category: selectedCategory
            })
          }
        );
        if (res.ok) {
          const data = await res.json();
          setFavorites(prev => [...prev, data.favorite]);
          setShorts(prev => prev.map(s => 
            s.id === short.id ? { ...s, is_favorited: true } : s
          ));
          toast.success('Added to favorites');
        }
      }
    } catch (err) {
      toast.error('Failed to update favorites');
    }
  };
  
  const shareVideo = async (short) => {
    const url = `https://www.youtube.com/shorts/${short.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: short.title,
          text: `Check out this video: ${short.title}`,
          url: url
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard');
  };
  
  const openInYouTube = (short) => {
    window.open(`https://www.youtube.com/shorts/${short.id}`, '_blank');
  };
  
  // Handle scroll navigation
  const handleScroll = useCallback((direction) => {
    if (direction === 'down' && currentIndex < shorts.length - 1) {
      setCurrentIndex(prev => prev + 1);
      
      // Load more when near end
      if (currentIndex >= shorts.length - 5 && nextPageToken && !loadingMore) {
        fetchShorts(nextPageToken);
      }
    } else if (direction === 'up' && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex, shorts.length, nextPageToken, loadingMore]);
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'j') {
        handleScroll('down');
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        handleScroll('up');
      } else if (e.key === 'm') {
        setIsMuted(prev => !prev);
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsPlaying(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleScroll]);
  
  // Touch/scroll handling
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    let startY = 0;
    let startTime = 0;
    
    const handleTouchStart = (e) => {
      startY = e.touches[0].clientY;
      startTime = Date.now();
    };
    
    const handleTouchEnd = (e) => {
      const endY = e.changedTouches[0].clientY;
      const diff = startY - endY;
      const timeDiff = Date.now() - startTime;
      
      // Swipe threshold
      if (Math.abs(diff) > 50 && timeDiff < 300) {
        if (diff > 0) {
          handleScroll('down');
        } else {
          handleScroll('up');
        }
      }
    };
    
    const handleWheel = (e) => {
      e.preventDefault();
      if (e.deltaY > 0) {
        handleScroll('down');
      } else {
        handleScroll('up');
      }
    };
    
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleScroll]);
  
  const currentShort = shorts[currentIndex];
  
  return (
    <div className="min-h-screen bg-black text-white" data-testid="doomscroll-page">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
        <div className="px-4 py-3 flex items-center justify-between pointer-events-auto">
          <h1 className="text-xl font-bold">Doomscroll</h1>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFavorites(true)}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              data-testid="favorites-btn"
            >
              <Heart size={20} className={favorites.length > 0 ? 'fill-red-500 text-red-500' : ''} />
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              data-testid="settings-btn"
            >
              <Settings size={20} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Category Pills */}
      <div className="fixed top-14 left-0 right-0 z-30 px-4 py-2 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2">
          {categories.map(cat => {
            const Icon = categoryIcons[cat.id] || Grid;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-white text-black'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <Icon size={14} />
                {cat.name}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Main Video Container */}
      <div 
        ref={containerRef}
        className="h-screen w-full flex items-center justify-center overflow-hidden"
      >
        {loading ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 size={48} className="animate-spin text-white/50" />
            <p className="text-white/60">Loading shorts...</p>
          </div>
        ) : shorts.length === 0 ? (
          <div className="flex flex-col items-center gap-4 px-4 text-center">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
              <Tv size={32} className="text-white/50" />
            </div>
            <p className="text-white/60">No shorts found</p>
            <p className="text-white/40 text-sm">Try a different category or add some channels</p>
            <button
              onClick={() => fetchShorts()}
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-full font-medium hover:bg-gray-200 transition-colors"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        ) : currentShort ? (
          <div className="relative w-full h-full max-w-md mx-auto">
            {/* Video Player (YouTube Embed) */}
            <div className="absolute inset-0 flex items-center justify-center bg-black">
              <iframe
                key={currentShort.id}
                src={`https://www.youtube.com/embed/${currentShort.id}?autoplay=${isPlaying ? 1 : 0}&mute=${isMuted ? 1 : 0}&loop=1&playlist=${currentShort.id}&controls=0&playsinline=1&rel=0&modestbranding=1`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={currentShort.title}
              />
            </div>
            
            {/* Overlay Controls */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Top gradient */}
              <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/60 to-transparent" />
              
              {/* Bottom info */}
              <div className="absolute bottom-0 left-0 right-0 p-4 pb-20 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                <h2 className="text-lg font-semibold line-clamp-2 mb-1">{currentShort.title}</h2>
                <p className="text-white/70 text-sm">{currentShort.channel_title}</p>
                {currentShort.view_count > 0 && (
                  <p className="text-white/50 text-xs mt-1">
                    {formatViewCount(currentShort.view_count)} views
                  </p>
                )}
              </div>
              
              {/* Right side actions */}
              <div className="absolute right-4 bottom-32 flex flex-col gap-4 pointer-events-auto">
                <button
                  onClick={() => toggleFavorite(currentShort)}
                  className="flex flex-col items-center gap-1"
                  data-testid="like-btn"
                >
                  <div className={`p-3 rounded-full ${currentShort.is_favorited ? 'bg-red-500' : 'bg-white/20'}`}>
                    <Heart size={24} className={currentShort.is_favorited ? 'fill-white' : ''} />
                  </div>
                  <span className="text-xs">{currentShort.like_count > 0 ? formatViewCount(currentShort.like_count) : 'Like'}</span>
                </button>
                
                <button
                  onClick={() => shareVideo(currentShort)}
                  className="flex flex-col items-center gap-1"
                  data-testid="share-btn"
                >
                  <div className="p-3 bg-white/20 rounded-full">
                    <Share2 size={24} />
                  </div>
                  <span className="text-xs">Share</span>
                </button>
                
                <button
                  onClick={() => openInYouTube(currentShort)}
                  className="flex flex-col items-center gap-1"
                  data-testid="youtube-btn"
                >
                  <div className="p-3 bg-white/20 rounded-full">
                    <ExternalLink size={24} />
                  </div>
                  <span className="text-xs">YouTube</span>
                </button>
                
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="flex flex-col items-center gap-1"
                  data-testid="mute-btn"
                >
                  <div className="p-3 bg-white/20 rounded-full">
                    {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
                  </div>
                  <span className="text-xs">{isMuted ? 'Unmute' : 'Mute'}</span>
                </button>
              </div>
              
              {/* Navigation arrows */}
              <div className="absolute left-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 pointer-events-auto">
                <button
                  onClick={() => handleScroll('up')}
                  disabled={currentIndex === 0}
                  className="p-2 bg-white/20 rounded-full disabled:opacity-30 hover:bg-white/30 transition-colors"
                >
                  <ChevronUp size={24} />
                </button>
                <button
                  onClick={() => handleScroll('down')}
                  disabled={currentIndex >= shorts.length - 1 && !nextPageToken}
                  className="p-2 bg-white/20 rounded-full disabled:opacity-30 hover:bg-white/30 transition-colors"
                >
                  <ChevronDown size={24} />
                </button>
              </div>
              
              {/* Progress indicator */}
              <div className="absolute top-24 left-4 right-4 pointer-events-none">
                <div className="flex gap-1">
                  {shorts.slice(0, Math.min(shorts.length, 10)).map((_, idx) => (
                    <div
                      key={idx}
                      className={`flex-1 h-1 rounded-full transition-colors ${
                        idx === currentIndex % 10 ? 'bg-white' : 'bg-white/30'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-center text-white/50 text-xs mt-2">
                  {currentIndex + 1} / {shorts.length}
                </p>
              </div>
            </div>
          </div>
        ) : null}
        
        {/* Loading more indicator */}
        {loadingMore && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
            <Loader2 size={24} className="animate-spin text-white/50" />
          </div>
        )}
      </div>
      
      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-gray-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-bold">Settings</h2>
              <button onClick={() => setShowSettings(false)} className="p-2 hover:bg-white/10 rounded-full">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4 overflow-auto max-h-[60vh]">
              {/* Autoplay toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Autoplay</p>
                  <p className="text-white/50 text-sm">Automatically play videos</p>
                </div>
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className={`w-12 h-6 rounded-full transition-colors ${isPlaying ? 'bg-green-500' : 'bg-white/20'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${isPlaying ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              
              {/* Sound toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Sound</p>
                  <p className="text-white/50 text-sm">Play videos with sound</p>
                </div>
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className={`w-12 h-6 rounded-full transition-colors ${!isMuted ? 'bg-green-500' : 'bg-white/20'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${!isMuted ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              
              {/* Keyboard shortcuts */}
              <div className="pt-4 border-t border-white/10">
                <p className="font-medium mb-3">Keyboard Shortcuts</p>
                <div className="space-y-2 text-sm text-white/70">
                  <div className="flex justify-between">
                    <span>Next video</span>
                    <kbd className="px-2 py-1 bg-white/10 rounded">↓ or J</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>Previous video</span>
                    <kbd className="px-2 py-1 bg-white/10 rounded">↑ or K</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>Toggle mute</span>
                    <kbd className="px-2 py-1 bg-white/10 rounded">M</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>Play/Pause</span>
                    <kbd className="px-2 py-1 bg-white/10 rounded">Space</kbd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Favorites Modal */}
      {showFavorites && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-gray-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-bold">Favorites ({favorites.length})</h2>
              <button onClick={() => setShowFavorites(false)} className="p-2 hover:bg-white/10 rounded-full">
                <X size={20} />
              </button>
            </div>
            
            <div className="overflow-auto max-h-[60vh]">
              {favorites.length === 0 ? (
                <div className="p-8 text-center">
                  <Heart size={32} className="mx-auto text-white/30 mb-3" />
                  <p className="text-white/60">No favorites yet</p>
                  <p className="text-white/40 text-sm">Like videos to save them here</p>
                </div>
              ) : (
                <div className="p-2">
                  {favorites.map(fav => (
                    <div 
                      key={fav.id || fav.video_id}
                      className="flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg cursor-pointer"
                      onClick={() => {
                        const idx = shorts.findIndex(s => s.id === fav.video_id);
                        if (idx >= 0) {
                          setCurrentIndex(idx);
                          setShowFavorites(false);
                        } else {
                          window.open(`https://www.youtube.com/shorts/${fav.video_id}`, '_blank');
                        }
                      }}
                    >
                      <img
                        src={fav.thumbnail_url}
                        alt={fav.title}
                        className="w-16 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium line-clamp-2 text-sm">{fav.title}</p>
                        <p className="text-white/50 text-xs mt-1">{fav.channel_title}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite({ id: fav.video_id, is_favorited: true });
                        }}
                        className="p-2 hover:bg-white/10 rounded-full"
                      >
                        <Heart size={16} className="fill-red-500 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to format view counts
const formatViewCount = (count) => {
  if (count >= 1000000) {
    return (count / 1000000).toFixed(1) + 'M';
  } else if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K';
  }
  return count.toString();
};

export default Doomscroll;
